import type { ReactNode } from "react";

import type { AssetDataBitfield, AssetDataVal } from "@/api/client";

/** The cluster instrument: two dials, the telltale strip, and the readouts.
 *
 *  PORTED FROM THE CLUSTER METER (bmsUdsTool/frontend/css/clusterLive.css),
 *  because a rider's cluster and a fleet console showing the same vehicle
 *  should not feel like two products. The geometry is that page's own: a
 *  1320/540 shell, dials at top 60% inset 15%, ring r=135 at stroke-width 14
 *  with a round cap, readout trios flanking each ring with the outer two
 *  nudged inward to follow its curve, and a mode circle over odometer and
 *  trip down the centre.
 *
 *  THE NUMERAL IS WEIGHT 200, which is what makes this read as an instrument
 *  rather than a statistic. The source sets Consolas on it; this console may
 *  not (tests/ui/test_one_typeface.py, and "No other font is allowed"), and it
 *  does not need to: Montserrat is embedded as a VARIABLE font at weight
 *  100-900, so the thin numeral is available in the one face, with tabular
 *  figures doing the job `.mono` means.
 *
 *  WHY PROPORTIONAL AND NOT THE SOURCE'S PIXELS. That page is 1320px wide by
 *  design; this panel is two thirds of a dashboard column. At ~900px the
 *  source's 110px readout column 36px from the edge OVERLAPS a dial inset at
 *  13% by 29px -- measured, not guessed -- so every offset here is a
 *  percentage and every size is `cqw` against the panel's own width. The
 *  panel is a container (`.panel` already declares `container-type`), so the
 *  instrument scales to the space it is given instead of to the window.
 *
 *  THE TELLTALES ARE REAL. An earlier pass drew them permanently dark on the
 *  belief that `status_bits` had no published per-bit map. It does: the
 *  firmware proto annotates all nine bits, `shared/lib/bitfields.py` carries
 *  the checked copy, and the server sends them decoded. Dattatray found that
 *  by asking -- *"Check proto buff data these values should be there"* -- and
 *  he was right. 007 reports 0x24, which is Headlight and Ready, so two of
 *  them light.
 *
 *  NO DECODER HERE. The bit layout lives in the firmware and in
 *  `shared/lib/bitfields.py`; the server decodes and this renders {name, on}.
 *  A third copy in the browser is how a renumbered bit looks right on screen
 *  while naming the wrong lamp.
 */

type Vals = Record<string, AssetDataVal>;
const num = (v: Vals, k: string): number | null =>
  typeof v[k] === "number" ? (v[k] as number) : null;

/** r=135, a 270-degree sweep opening at 135 degrees: the source's geometry. */
const R = 135, START = 135, SWEEP = 270;
const polar = (r: number, deg: number): [number, number] =>
  [r * Math.cos((deg * Math.PI) / 180), r * Math.sin((deg * Math.PI) / 180)];

function Dial({ side, value, max, unit, label, tone, steps, majorEvery, title }: {
  side: "l" | "r";
  value: number | null; max: number;
  unit: string; label: string;
  tone?: "ok" | "warn" | "bad";
  steps: number; majorEvery: number;
  title?: string;
}) {
  const [x0, y0] = polar(R, START);
  const [x1, y1] = polar(R, START + SWEEP);
  const arc = `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${R} ${R} 0 1 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
  // the full arc's length, revealed by dashoffset -- how the source animates
  // it, and what keeps the round cap intact at every value
  const len = (SWEEP / 360) * 2 * Math.PI * R;
  const frac = value == null ? 0 : Math.max(0, Math.min(1, value / max));

  const ticks = [];
  for (let i = 0; i <= steps; i++) {
    const major = i % majorEvery === 0;
    const deg = START + SWEEP * (i / steps);
    const [ax, ay] = polar(major ? 143 : 149, deg);
    const [bx, by] = polar(159, deg);
    ticks.push(<line key={`t${i}`} className={major ? "lw-tick major" : "lw-tick"}
      x1={ax.toFixed(2)} y1={ay.toFixed(2)} x2={bx.toFixed(2)} y2={by.toFixed(2)} />);
    if (major) {
      const [lx, ly] = polar(176, deg);
      ticks.push(<text key={`l${i}`} className="lw-ticklbl" x={lx.toFixed(2)} y={ly.toFixed(2)}>
        {Math.round(max * (i / steps))}
      </text>);
    }
  }

  return (
    <div className={`lw-dial lw-dial-${side}`} title={title}>
      <svg viewBox="-170 -170 340 340" role="img"
        aria-label={`${label} ${value == null ? "not reported" : value} ${unit}`}>
        <circle className="lw-ring-bg" cx="0" cy="0" r={R} />
        <g>{ticks}</g>
        <path className={`lw-ring${tone ? ` ${tone}` : ""}`} d={arc}
          style={{ strokeDasharray: len, strokeDashoffset: len * (1 - frac) }} />
      </svg>
      <div className="lw-dc">
        {/* a dash, not a zero: a device that has not reported is not at rest */}
        <div className="lw-dv">{value == null ? "—" : Math.round(value)}</div>
        <div className="lw-du">{unit}</div>
        <div className="lw-dl">{label}</div>
      </div>
    </div>
  );
}

/** The nine telltales, in the order a cluster shows them: the two indicators
 *  flanking the headlight, then the body states with READY in the middle.
 *
 *  Each is drawn whether it is lit or not, because a cluster with one lamp on
 *  it tells you nothing about what else it can tell you. `Ready` is the pill
 *  rather than a glyph, which is how the source distinguishes the state the
 *  rider acts on from the states they merely observe. */
const LAMP_ICON: Record<string, ReactNode> = {
  "Indicator Left": (
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M14 4 L6 12 L14 20 Z" /></svg>),
  "Indicator Right": (
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M10 4 L18 12 L10 20 Z" /></svg>),
  Headlight: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 5 L10 19 Q22 19 22 12 Q22 5 10 5 Z" />
      <path d="M2 8 H8 M2 11 H7 M2 14 H6 M2 17 H5" />
    </svg>),
  "Side Stand": (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 5 L8 7 M21 5 L16 7 M8 7 H16 M12 7 V9.5" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M10 14.5 H14 V19 H10 Z M11 19 L9 22 M13 19 L15 22" />
    </svg>),
  Brake: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round">
      <circle cx="12" cy="12" r="9" /><path d="M12 7 V13 M12 15.5 V16" />
    </svg>),
  Reverse: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9" />
      <text x="12" y="16.5" textAnchor="middle" fontSize="12" fontWeight="900"
        fill="currentColor" stroke="none">R</text>
    </svg>),
  Cruise: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round">
      <circle cx="12" cy="12" r="9" /><path d="M12 12 L16 9" />
    </svg>),
  Regen: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3 V9 M9 6 L12 3 L15 6" /><path d="M5 13 A7 7 0 1 0 19 13" />
    </svg>),
};

const ROW_1 = ["Indicator Left", "Headlight", "Indicator Right"];
const ROW_2 = ["Side Stand", "Brake", "Ready", "Reverse", "Cruise", "Regen"];

function Strip({ bf }: { bf: AssetDataBitfield | undefined }) {
  const on = new Map((bf?.bits ?? []).map((b) => [b.name ?? "", b.on]));
  const title = bf
    ? `status_bits = 0x${bf.word.toString(16).padStart(2, "0")}`
    : "status_bits not reported by this device";
  const lamp = (name: string) => {
    if (name === "Ready") {
      return (
        <span key={name} className={`lw-ready${on.get(name) ? " on" : ""}`}
          title={`${name}: ${on.get(name) ? "set" : "clear"}`}>Ready</span>
      );
    }
    return (
      <span key={name} className={`lw-lamp${on.get(name) ? " on" : ""}`}
        title={`${name}: ${on.has(name) ? (on.get(name) ? "set" : "clear") : "not reported"}`}>
        {LAMP_ICON[name]}
      </span>
    );
  };
  return (
    <div className="lw-strip" title={title}>
      <div className="lw-row">{ROW_1.map(lamp)}</div>
      <div className="lw-row">{ROW_2.map(lamp)}</div>
    </div>
  );
}

function Stat({ k, v, tone, title }: {
  k: string; v: string; tone?: "ok" | "warn"; title?: string;
}) {
  return (
    <div className="lw-s" title={title}>
      <div className="lw-sk">{k}</div>
      <div className={`lw-sv${tone ? ` ${tone}` : ""}`}>{v}</div>
    </div>
  );
}

/** Mode names in gear order. The wire sends an integer; this is the only place
 *  that turns it into a word, and an unknown value stays a number rather than
 *  being rounded to the nearest known mode. */
const MODES = ["Park", "Eco", "City", "Sport", "Reverse"];
const MODE_CLASS = ["park", "economy", "city", "sport", "reverse"];

export function Instrument({ v, soc, speed, bits, children }: {
  v: Vals;
  soc: number | null;
  speed: number | null;
  /** every bitfield the device reported, decoded by the server */
  bits: Record<string, AssetDataBitfield>;
  /** the fault grid, rendered flush inside the frame */
  children?: ReactNode;
}) {
  const packI = num(v, "pack_i");
  // pack_i_decia is documented "+ = discharge", so a NEGATIVE current is the
  // pack charging. Getting this backwards puts an arrow on the wrong way.
  const charging = packI != null && packI < 0;
  const gear = num(v, "gear");
  const mode = gear != null && gear >= 0 && gear < MODES.length ? MODES[gear] : gear != null ? `Gear ${gear}` : "—";
  const modeCls = gear != null && gear >= 0 && gear < MODE_CLASS.length ? MODE_CLASS[gear] : "";
  const odo = num(v, "odometer_m");
  const trip = num(v, "trip_m");
  const supply = num(v, "supply_v_mv");
  const battT = num(v, "battery_temp_decic");
  const hottest = num(v, "temp_max_c");
  const dash = (n: number | null, f: (x: number) => string) => (n == null ? "—" : f(n));

  return (
    <div className="lw-frame">
      <div className="lw-shell">
        <Strip bf={bits.status_bits} />

        <Dial side="l" value={soc} max={100} unit="percent" label="Battery"
          tone={soc == null ? undefined : soc <= 15 ? "bad" : soc <= 35 ? "warn" : "ok"}
          steps={10} majorEvery={2}
          title="State of charge, as the BMS reports it" />
        <Dial side="r" value={speed} max={120} unit="km/h" label="Speed"
          steps={12} majorEvery={3} title="Road speed" />

        {/* the source's own left trio: voltage, current, battery temperature */}
        <div className="lw-sc lw-sc-l">
          <Stat k="Voltage" v={dash(num(v, "pack_v"), (x) => `${x.toFixed(1)} V`)} />
          <Stat k="Current"
            v={packI == null ? "—" : `${charging ? "↓" : "↑"} ${Math.abs(packI).toFixed(1)} A`}
            tone={charging ? "ok" : undefined} />
          <Stat k="Batt Temp" v={dash(battT, (x) => `${(x / 10).toFixed(1)} °C`)} />
        </div>

        <div className="lw-sc lw-sc-r">
          {/* temp_max_c is max(motor, controller, battery) -- the hottest of
              three subsystems, so it is labelled as that and not as a pack
              figure. The three individually are fields 18-20. */}
          <Stat k="Hottest" v={dash(hottest, (x) => `${x.toFixed(1)} °C`)} />
          <Stat k="Motor RPM" v={dash(num(v, "motor_rpm"), (x) => String(Math.round(x)))} />
          <Stat k="Supply" v={dash(supply, (x) => `${(x / 1000).toFixed(1)} V`)} />
        </div>

        <div className="lw-ctr">
          <span className={`lw-gear ${modeCls}`}><span className="lw-gear-t">{mode}</span></span>
          <div className="lw-trip">
            <div className="lw-tk">Odometer</div>
            <div className="lw-tv">{dash(odo, (x) => `${(x / 1000).toFixed(1)} km`)}</div>
          </div>
          <div className="lw-trip">
            <div className="lw-tk">Trip</div>
            <div className="lw-tv">{dash(trip, (x) => `${(x / 1000).toFixed(1)} km`)}</div>
            {trip != null && trip > 0 && <span className="lw-ts" data-status="active">Active</span>}
          </div>
        </div>
      </div>
      {children}
    </div>
  );
}
